// 1. React
import React, { useState, useEffect, useCallback } from 'react';

// 2. Third-party
import { Row, Col, Select, Input, Button, Tag, Table, Space, Popconfirm, message } from 'antd';
import { EyeOutlined, DeleteOutlined, CheckCircleOutlined, StopOutlined, ImportOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';

// 3. Components
import Box from '@iso/components/utility/box';
import IntlMessages from '@iso/components/utility/intlMessages';

// 4. Services
import { globalFormService, categoryGlobalFormService, subCategoryGlobalFormService } from '@iso/services';

// 5. Constants
import {
  LIBRARY_FORM_TYPE_OPTIONS,
  LIBRARY_FORM_TYPE_COLORS,
  getLibraryFormTypeLabel,
} from '@iso/constants/libraryFormTypes';

// 6. Local components
import ImportFormsModal from './ImportFormsModal';
import ImportResultModal from './ImportResultModal';
import PreviewFormModal from './PreviewFormModal';
import LibraryFormBuilderDrawer from './LibraryFormBuilderDrawer';

// 7. Styles
import ManageLibraryFormsPage from './ManageLibraryForms.styles';

const SUCCESS_CODE = '0000';
const DEFAULT_PAGE_SIZE = 25;

const FormTemplatesTab = () => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filters
  const [type, setType] = useState(undefined);
  const [categoryId, setCategoryId] = useState(undefined);
  const [subCategoryId, setSubCategoryId] = useState(undefined);
  const [search, setSearch] = useState('');

  // Sorting (server-side)
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter option lists
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  // Modals / drawer
  const [importVisible, setImportVisible] = useState(false);
  const [resultVisible, setResultVisible] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [builderVisible, setBuilderVisible] = useState(false);
  const [builderForm, setBuilderForm] = useState(null);

  const fetchForms = useCallback(() => {
    setLoading(true);
    globalFormService
      .list({
        page,
        limit: pageSize,
        type,
        category_global_form_id: categoryId,
        sub_category_global_form_id: subCategoryId,
        search,
        sort_field: sortField,
        sort_order: sortOrder,
      })
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setDataSource(res.global_forms || []);
          setTotal(res.pagination ? res.pagination.totalCount : 0);
        } else {
          message.error((res && res.message) || 'Failed to load forms');
        }
      })
      .catch((error) => {
        console.error('Load forms error:', error);
        message.error('Failed to load forms');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, pageSize, type, categoryId, subCategoryId, search, sortField, sortOrder]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const loadCategories = useCallback(() => {
    categoryGlobalFormService.get().then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        setCategoryOptions((res.category_global_form || []).map((c) => ({ value: c.id, label: c.name })));
      }
    });
  }, []);

  const loadSubCategories = useCallback((id) => {
    if (!id) {
      setSubCategoryOptions([]);
      return;
    }
    subCategoryGlobalFormService.get(id).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        setSubCategoryOptions((res.sub_category_global_forms || []).map((s) => ({ value: s.id, label: s.name })));
      }
    });
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadSubCategories(categoryId);
  }, [categoryId, loadSubCategories]);

  const handleTypeChange = (value) => {
    setType(value);
    setPage(1);
  };

  const handleCategoryChange = (value) => {
    setCategoryId(value);
    setSubCategoryId(undefined);
    setPage(1);
  };

  const handleSubCategoryChange = (value) => {
    setSubCategoryId(value);
    setPage(1);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);

    const s = sorter || {};
    const field = s.field || s.columnKey || sortField;
    let order;
    if (s.order === 'ascend') {
      order = 'asc';
    } else if (s.order === 'descend') {
      order = 'desc';
    } else {
      // antd's 3rd click clears the sort; flip instead so sorting stays a
      // stable 2-state toggle (asc <-> desc) and never snaps back to a default.
      order = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortOrder(order);
  };

  const sortOrderFor = (field) =>
    (sortField === field ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null);

  const handleTogglePublish = (record) => {
    const nextValue = record.is_live ? 0 : 1;
    globalFormService.setLive(record.id, nextValue).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        message.success(nextValue ? 'Form published' : 'Form unpublished');
        fetchForms();
      } else {
        message.error((res && res.message) || 'Failed to update form');
      }
    });
  };

  const handleDelete = (id) => {
    globalFormService.destroy(id).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        message.success('Form deleted');
        fetchForms();
      } else {
        message.error((res && res.message) || 'Failed to delete form');
      }
    });
  };

  const handlePreview = (record) => {
    setPreviewForm(record);
    setPreviewVisible(true);
  };

  const handleAddForm = () => {
    setBuilderForm(null);
    setBuilderVisible(true);
  };

  const handleEditForm = (record) => {
    setBuilderForm(record);
    setBuilderVisible(true);
  };

  const columns = [
    {
      title: <IntlMessages id="adminPage.libraryForm.title" />,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortOrderFor('name'),
    },
    {
      title: <IntlMessages id="adminPage.libraryForm.type" />,
      dataIndex: 'type',
      key: 'type',
      render: (value) => (value ? <Tag color={LIBRARY_FORM_TYPE_COLORS[value]}>{getLibraryFormTypeLabel(value)}</Tag> : '-'),
    },
    {
      title: <IntlMessages id="adminPage.libraryForm.category" />,
      dataIndex: 'category_name',
      key: 'category_name',
      render: (value) => value || '-',
    },
    {
      title: <IntlMessages id="adminPage.libraryForm.subCategory" />,
      dataIndex: 'sub_category_name',
      key: 'sub_category_name',
      render: (value) => value || '-',
    },
    {
      title: <IntlMessages id="adminPage.libraryForm.status" />,
      dataIndex: 'is_live',
      key: 'is_live',
      render: (value) => (
        <Tag color={value ? 'green' : 'default'}>
          {value ? <IntlMessages id="adminPage.libraryForm.live" /> : <IntlMessages id="adminPage.libraryForm.draft" />}
        </Tag>
      ),
    },
    {
      title: <IntlMessages id="adminPage.libraryForm.createdAt" />,
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortOrder: sortOrderFor('created_at'),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: <IntlMessages id="adminPage.libraryForm.action" />,
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handlePreview(record)} />
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditForm(record)} />
          <Button
            type="link"
            icon={record.is_live ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleTogglePublish(record)}
          />
          <Popconfirm
            title={<IntlMessages id="adminPage.libraryForm.confirmDelete" />}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <ManageLibraryFormsPage>
        <div className="button-group">
          <Button icon={<ImportOutlined />} onClick={() => setImportVisible(true)}>
            Import Forms (JSON)
          </Button>
          <Button type="primary" className="btn-success" icon={<PlusOutlined />} onClick={handleAddForm}>
            Add Form
          </Button>
        </div>

        <Row className="filter-bar" gutter={[8, 8]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              placeholder="Type"
              value={type}
              options={LIBRARY_FORM_TYPE_OPTIONS}
              onChange={handleTypeChange}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Category"
              value={categoryId}
              options={categoryOptions}
              onChange={handleCategoryChange}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Sub-category"
              value={subCategoryId}
              options={subCategoryOptions}
              onChange={handleSubCategoryChange}
              disabled={!categoryId}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input.Search allowClear placeholder="Search title" onSearch={handleSearch} enterButton />
          </Col>
        </Row>

        <Box id="containerBox">
          <Table
            rowKey="id"
            dataSource={dataSource}
            columns={columns}
            size="small"
            loading={loading}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
            }}
            onChange={handleTableChange}
          />
        </Box>
      </ManageLibraryFormsPage>

      <ImportFormsModal
        visible={importVisible}
        onCancel={() => setImportVisible(false)}
        onImported={(summary) => {
          setImportVisible(false);
          setImportResult(summary);
          setResultVisible(true);
          fetchForms();
        }}
      />

      <ImportResultModal
        visible={resultVisible}
        result={importResult}
        onCancel={() => setResultVisible(false)}
      />

      <PreviewFormModal
        visible={previewVisible}
        form={previewForm}
        onCancel={() => setPreviewVisible(false)}
      />

      <LibraryFormBuilderDrawer
        visible={builderVisible}
        form={builderForm}
        onClose={() => setBuilderVisible(false)}
        onSaved={fetchForms}
      />
    </>
  );
};

export default FormTemplatesTab;
